import { API_URL } from '@goodfellas/environment'
import type { FeedbackUser } from '@goodfellas/feedback'
import * as Belt from '@goodfellas/graduation'
import type { Colors } from '@goodfellas/graduation'

import { goodfellasApi } from '../apis'
import { FighterType } from '../types/fighter'
import { normalizeBeltColor, normalizeDegree } from './graduation'
import {
  CardsState,
  FeedbackState,
  FighterCard,
  TableGroupType,
  TableState,
  User,
} from './store'
import { isEqualWithNinthDigit } from './utils'

const normalizeBelt = (belt?: unknown): string => normalizeBeltColor(belt) || ''

export const fetchTableData = async (phoneNumber: string, groupId?: string) => {
  try {
    const groupIdQuery = groupId ? `?groupId=${groupId}` : ''
    const response = await goodfellasApi.get(
      `${API_URL}evaluation/tables/${phoneNumber}${groupIdQuery}`,
    )

    if (response.status !== 200) {
      throw new Error(`Failed to fetch table data: ${response.status}`)
    }
    return response.data
  } catch (error) {
    throw new Error(error)
  }
}

export const fetchFeedbackData = async (
  phoneNumber: string,
  groupId?: string,
): Promise<FeedbackUser | null> => {
  const groupIdQuery = groupId ? `?groupId=${groupId}` : ''
  try {
    const response = await goodfellasApi.get(
      `${API_URL}evaluation/feedback/${phoneNumber}${groupIdQuery}`,
    )

    if (response.status !== 200) {
      throw new Error(`Failed to fetch table data: ${response.status}`)
    }
    return response.data
  } catch (error) {
    if (error instanceof Error && 'response' in error) {
      const axiosErr = error as {
        response?: { status?: number; data?: { error?: string } }
      }
      if (axiosErr.response?.status === 403) {
        const message =
          axiosErr.response?.data?.error ??
          'Você precisa ter conta no app para participar da avaliação.'
        const err = Object.assign(new Error(message), { status: 403 })
        throw err
      }
    }
    return null
  }
}

export const fetchCardsData = async (phoneNumber: string, groupId?: string) => {
  const groupIdQuery = groupId ? `?groupId=${groupId}` : ''
  try {
    const response = await goodfellasApi.get(
      `${API_URL}evaluation/cards/${phoneNumber}${groupIdQuery}`,
    )

    if (response.status !== 200) {
      throw new Error(`Failed to fetch table data: ${response.status}`)
    }
    return response.data
  } catch (_error) {
    return null
  }
}

export const fetchMyGlobalCard = async () => {
  try {
    const response = await goodfellasApi.get(`${API_URL}evaluation/cards/me`)
    if (response.status !== 200) {
      throw new Error(`Failed to fetch global card: ${response.status}`)
    }
    return response.data
  } catch (_error) {
    return null
  }
}

export const fetchGlobalCardByPhone = async (phoneNumber: string) => {
  try {
    const response = await goodfellasApi.get(
      `${API_URL}evaluation/cards/global/${phoneNumber}`,
    )
    if (response.status !== 200) {
      throw new Error(`Failed to fetch global card: ${response.status}`)
    }
    return response.data
  } catch (_error) {
    return null
  }
}

export const searchCardsPeople = async (query: string) => {
  const q = query.trim()
  if (q.length < 2) return []
  try {
    const response = await goodfellasApi.get(
      `${API_URL}evaluation/cards/search`,
      {
        params: { q },
      },
    )
    if (response.status !== 200) return []
    return response.data || []
  } catch (_error) {
    return []
  }
}

export const fetchGroups = async () => {
  try {
    const response = await goodfellasApi.get<TableGroupType[]>(
      `${API_URL}evaluation/groups`,
    )

    return response.data
  } catch (_error) {
    return []
  }
}

export const setTableData = async ({
  tableHook,
  userData,
}: {
  userData: User
  tableHook: TableState
}): Promise<void> => {
  const data = await fetchTableData(userData.phoneNumber, userData?.group?._id)
  const user = data.users.find((u: { phoneNumber: string }) =>
    isEqualWithNinthDigit(u.phoneNumber, userData.phoneNumber),
  )

  if (!user) return

  const tableGroup = {
    _id: data._id,
    groupName: data.group,
    fighters: data.users.map((user) => ({
      phoneNumber: user.phoneNumber,
      name: user.name,
      belt: normalizeBelt(user.belt),
      degree: normalizeDegree(normalizeBeltColor(user.belt), user.degree),
      type: user.type,
    })),
  }
  tableHook.setGroup(tableGroup)
}

export const setFeedbackData = async ({
  data,
  feedbackHook,
  userData,
}: {
  data: FeedbackUser
  feedbackHook: FeedbackState
  userData: User
}): Promise<void> => {
  try {
    const resolvedBelt: Colors | '' =
      normalizeBeltColor(data.belt) || normalizeBeltColor(userData.belt) || ''
    const resolvedDegree = normalizeDegree(
      resolvedBelt,
      data.degree ?? userData.degree ?? 0,
    )

    const feedbacks = {
      date: data.date,
      phoneNumber: userData.phoneNumber,
      name: data.name,
      belt: resolvedBelt,
      degree: resolvedDegree,
      type: data.type,
      ratings: data.ratings.map((fighter) => ({
        fighterPhone: fighter.fighterPhone,
        fighterName: fighter.fighterName,
        fighterBelt:
          normalizeBeltColor(fighter.fighterBelt) || Belt.Colors.White,
        fighterBeltDegree: fighter.fighterBeltDegree || 0,
        fighterType: fighter.fighterType || FighterType.STUDENT,
        pointsLeft: fighter.pointsLeft,
        gameStyle: fighter.gameStyle,
        personalSkills: {
          technique: fighter.personalSkills.technique,
          strength: fighter.personalSkills.strength,
          agility: fighter.personalSkills.agility,
          stamina: fighter.personalSkills.stamina,
          commitment: fighter.personalSkills.commitment,
          focus: fighter.personalSkills.focus,
        },
        gameSkills: {
          guard: fighter.gameSkills.guard,
          guardRecovery: fighter.gameSkills.guardRecovery,
          guardPassing: fighter.gameSkills.guardPassing,
          sweep: fighter.gameSkills.sweep,
          takedown: fighter.gameSkills.takedown,
          submission: fighter.gameSkills.submission,
          submissionDefense: fighter.gameSkills.submissionDefense,
          positionDefense: fighter.gameSkills.positionDefense,
        },
        isRated:
          Object.values(fighter?.gameSkills).some((skill) => skill != 0) ||
          Object.values(fighter?.personalSkills).some((skill) => skill != 0),
      })),
      index: 0, // Add the missing index property
      feedbackActions: feedbackHook.feedbackActions,
    }

    feedbackHook.feedbackActions.setFeedback(feedbacks)
    feedbackHook.feedbackActions.setError(null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    // Surface the error to UI (e.g., require_account 403)
    feedbackHook.feedbackActions.setError(
      err?.message ??
        'Não foi possível carregar a avaliação. Tente novamente em alguns instantes.',
    )
  }
}

export const setCardsData = async ({
  cardsHook,
  userData,
  cardAlert,
  onCardUnavailable,
}: {
  cardsHook: CardsState
  userData: User
  cardAlert: string
  onCardUnavailable?: (message: string) => void
}): Promise<void> => {
  const [groupCards, globalCard] = await Promise.all([
    fetchCardsData(userData.phoneNumber, userData?.group?._id),
    fetchMyGlobalCard(),
  ])

  const groupList = (groupCards || []) as FighterCard[]
  const global = globalCard as FighterCard | null

  // Merge: keep group cards as "team cards", but ensure the user's global card is always present
  // so the screen can show the same card in any group.
  // When global is null (no real evaluation), exclude current user from list so we don't
  // auto-reveal an empty card from the group.
  const merged = (() => {
    if (!global?.phoneNumber) {
      return groupList.filter(
        (c) => !isEqualWithNinthDigit(c.phoneNumber, userData.phoneNumber),
      )
    }
    const withoutMe = groupList.filter(
      (c) => !isEqualWithNinthDigit(c.phoneNumber, global.phoneNumber),
    )
    return [global, ...withoutMe]
  })()

  const normalizedCards = (merged as FighterCard[]).map((c) => ({
    ...c,
    belt: normalizeBeltColor(c.belt) || '',
  }))

  if (!global?.phoneNumber) {
    if (onCardUnavailable) onCardUnavailable(cardAlert)
  }

  // Step only; do not override canonical profile fields here.
  userData.setUser({ ...userData, step: 'cards' })

  cardsHook.cardsActions.setCards(normalizedCards as FighterCard[])
  if (global?.phoneNumber) {
    const normalizedGlobal = normalizedCards.find((c) =>
      isEqualWithNinthDigit(c.phoneNumber, global.phoneNumber),
    )
    cardsHook.cardsActions.setSelectedFighter(
      (normalizedGlobal as FighterCard) || (global as FighterCard),
    )
  }
  cardsHook.cardsActions.setBeltAveragesAsBenchmark()
}
